import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";

type Job = {
  id: string;
  purpose: string;
  subject_type: string | null;
  subject_id: string | null;
  status: string;
  parts_expected: number;
  parts_received: number;
  attempt: number;
  error_text: string | null;
  created_at: string;
  completed_at: string | null;
};

type Part = {
  id: string;
  part_key: string;
  display_name: string | null;
  status: string;
  footer_confirmed: boolean | null;
  page_count: number | null;
  sort_order: number;
};

type JobEvent = {
  id: string;
  event_type: string;
  from_status: string | null;
  to_status: string | null;
  created_at: string;
};

const statusTone: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  complete: "default",
  queued: "secondary",
  sent: "secondary",
  rendering: "secondary",
  partial: "secondary",
  stalled: "destructive",
  failed: "destructive",
  cancelled: "outline",
};

function when(value: string | null) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

export default function DocumentJobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [flagOn, setFlagOn] = useState<boolean | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [parts, setParts] = useState<Part[]>([]);
  const [events, setEvents] = useState<JobEvent[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [cancelJob, setCancelJob] = useState<Job | null>(null);
  const [cancelText, setCancelText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [jobsRes, flagRes] = await Promise.all([
      supabase
        .from("docgen_jobs")
        .select("id, purpose, subject_type, subject_id, status, parts_expected, parts_received, attempt, error_text, created_at, completed_at")
        .order("created_at", { ascending: false })
        .limit(100),
      supabase.from("feature_flags").select("is_enabled").eq("flag_key", "docgen_generic_lane").maybeSingle(),
    ]);
    if (jobsRes.error) toast.error("The job list could not be loaded.");
    setJobs((jobsRes.data ?? []) as Job[]);
    setFlagOn(flagRes.data ? flagRes.data.is_enabled === true : false);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (job: Job) => {
    if (openId === job.id) { setOpenId(null); return; }
    setOpenId(job.id);
    setParts([]);
    setEvents([]);
    const [p, e] = await Promise.all([
      supabase.from("docgen_job_parts").select("id, part_key, display_name, status, footer_confirmed, page_count, sort_order").eq("job_id", job.id).order("sort_order", { ascending: true }),
      supabase.from("docgen_job_events").select("id, event_type, from_status, to_status, created_at").eq("job_id", job.id).order("created_at", { ascending: false }),
    ]);
    setParts((p.data ?? []) as Part[]);
    setEvents((e.data ?? []) as JobEvent[]);
  };

  const callAction = async (action: string, jobId: string) => {
    setBusyId(jobId);
    try {
      const { data, error } = await supabase.functions.invoke("docgen-retry", {
        body: { action, job_id: jobId },
      });
      if (error) {
        let message = error.message;
        try {
          const body = await (error as unknown as { context?: Response }).context?.json();
          if (body?.error) message = body.error;
        } catch { /* keep the original message */ }
        throw new Error(message);
      }
      const note = (data as { note?: string | null })?.note;
      toast.success(note ? `Done. ${note}` : "Done.");
      await load();
    } catch (err) {
      toast.error((err as Error).message || "Something went wrong.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Document Jobs</h1>
          <p className="text-sm text-muted-foreground">
            Every job we have sent to the document writing service, newest first.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Generic lane</CardTitle>
          <CardDescription>
            This switch lives in feature flags. It is shown here for reading only.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Badge variant={flagOn ? "default" : "outline"}>
            docgen_generic_lane {flagOn === null ? "unknown" : flagOn ? "on" : "off"}
          </Badge>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-10 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
            </div>
          ) : jobs.length === 0 ? (
            <p className="p-10 text-center text-sm text-muted-foreground">No jobs yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Purpose</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Parts</TableHead>
                    <TableHead>Attempt</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Finished</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job) => (
                    <>
                      <TableRow key={job.id} className="cursor-pointer" onClick={() => toggle(job)}>
                        <TableCell>
                          {openId === job.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </TableCell>
                        <TableCell className="font-medium">{job.purpose}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {job.subject_type ? `${job.subject_type}` : "-"}
                          {job.subject_id ? <div className="font-mono">{job.subject_id.slice(0, 8)}</div> : null}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusTone[job.status] ?? "secondary"}>{job.status}</Badge>
                        </TableCell>
                        <TableCell>{job.parts_received} of {job.parts_expected}</TableCell>
                        <TableCell>{job.attempt}</TableCell>
                        <TableCell className="max-w-[16rem] truncate text-xs text-muted-foreground">
                          {job.error_text ?? "-"}
                        </TableCell>
                        <TableCell className="text-xs">{when(job.created_at)}</TableCell>
                        <TableCell className="text-xs">{when(job.completed_at)}</TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyId === job.id || !["stalled", "failed"].includes(job.status) || job.attempt >= 5}
                              onClick={() => callAction("resend", job.id)}
                            >
                              Resend
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busyId === job.id || ["complete", "cancelled"].includes(job.status)}
                              onClick={() => { setCancelJob(job); setCancelText(""); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {openId === job.id && (
                        <TableRow key={`${job.id}-detail`}>
                          <TableCell colSpan={10} className="bg-muted/40">
                            <div className="grid gap-6 p-2 md:grid-cols-2">
                              <div>
                                <h3 className="mb-2 text-sm font-semibold">Parts</h3>
                                {parts.length === 0 ? (
                                  <p className="text-sm text-muted-foreground">No parts recorded.</p>
                                ) : (
                                  <ul className="space-y-1 text-sm">
                                    {parts.map((p) => (
                                      <li key={p.id} className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium">{p.display_name || p.part_key}</span>
                                        <Badge variant="secondary">{p.status}</Badge>
                                        <span className="text-xs text-muted-foreground">
                                          footer {p.footer_confirmed === true ? "confirmed" : p.footer_confirmed === false ? "missing" : "unknown"}
                                          {typeof p.page_count === "number" ? `, ${p.page_count} pages` : ""}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div>
                                <h3 className="mb-2 text-sm font-semibold">History</h3>
                                {events.length === 0 ? (
                                  <p className="text-sm text-muted-foreground">No history yet.</p>
                                ) : (
                                  <ul className="space-y-1 text-sm">
                                    {events.map((ev) => (
                                      <li key={ev.id} className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium">{ev.event_type}</span>
                                        {ev.from_status || ev.to_status ? (
                                          <span className="text-xs text-muted-foreground">
                                            {ev.from_status ?? "none"} to {ev.to_status ?? "none"}
                                          </span>
                                        ) : null}
                                        <span className="text-xs text-muted-foreground">{when(ev.created_at)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!cancelJob} onOpenChange={(open) => { if (!open) setCancelJob(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this job</DialogTitle>
            <DialogDescription>
              This stops the job. Anything already written stays where it is. Type CANCEL to go ahead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-confirm">Confirmation</Label>
            <Input
              id="cancel-confirm"
              value={cancelText}
              onChange={(e) => setCancelText(e.target.value)}
              placeholder="CANCEL"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelJob(null)}>Keep the job</Button>
            <Button
              variant="destructive"
              disabled={cancelText !== "CANCEL" || busyId === cancelJob?.id}
              onClick={async () => {
                const target = cancelJob;
                setCancelJob(null);
                if (target) await callAction("cancel", target.id);
              }}
            >
              Cancel the job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
